import { Loading, SkeletonBlock } from "@repo/ui/components/skeleton";
import { cn } from "@repo/ui/lib/utils";

// Page skeletons for the site (loading states round 3, L5): what a page shows
// while the server renders the next one, shaped like that page, so a tap
// answers at once instead of leaving the old page up. Same rules as /app's
// skeletons: hidden for 150 ms, then a fade-in; the blocks pulse.

const card = "bg-background rounded-card shadow-sm";

/** A page title and its lead line. */
function TitleSkeleton() {
  return (
    <div className="mb-5 flex flex-col gap-2">
      <SkeletonBlock className="h-7 w-48" />
      <SkeletonBlock className="h-4 w-72 max-w-full" />
    </div>
  );
}

/** Product cards (ProductCard): a square photo, then title, subtitle, price. */
export function ProductGridSkeleton({ count = 8, className }: { count?: number; className?: string }) {
  return (
    <div className={cn("grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4", className)}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className={cn(card, "flex flex-col overflow-hidden")}>
          <SkeletonBlock className="aspect-square w-full rounded-none" />
          <div className="flex flex-col gap-2 p-3">
            <SkeletonBlock className="h-4 w-4/5" />
            <SkeletonBlock className="h-3 w-1/2" />
            <SkeletonBlock className="mt-2 h-5 w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** /store and its facets: title, the metal switch, the chips, the grid. */
export function StoreSkeleton() {
  return (
    <Loading className="gap-0">
      <TitleSkeleton />
      <SkeletonBlock className="mb-3 h-10 w-full max-w-sm rounded-full" />
      <div className="mb-5 flex gap-2 overflow-hidden py-1">
        {["w-16", "w-20", "w-16", "w-24", "w-20"].map((w, i) => (
          <SkeletonBlock key={i} className={cn("h-8 shrink-0 rounded-full", w)} />
        ))}
      </div>
      <SkeletonBlock className="mb-3 h-3 w-40" />
      <ProductGridSkeleton />
    </Loading>
  );
}

/** A product page: crumbs, the photo, title and chips, the price panel, specs. */
export function ProductSkeleton() {
  return (
    <Loading className="gap-0">
      <SkeletonBlock className="mb-4 h-3 w-56" />
      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-8">
        <SkeletonBlock className="aspect-square w-full rounded-card" />
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <SkeletonBlock className="h-7 w-3/4" />
            <div className="flex gap-2">
              <SkeletonBlock className="h-6 w-16 rounded-full" />
              <SkeletonBlock className="h-6 w-20 rounded-full" />
            </div>
          </div>
          <div className={cn(card, "flex flex-col gap-3 p-4 sm:p-5")}>
            <SkeletonBlock className="h-3 w-20" />
            <SkeletonBlock className="h-8 w-44" />
            <SkeletonBlock className="h-10 w-full rounded-full" />
          </div>
          <div className={cn(card, "flex flex-col gap-4 p-4 sm:p-5")}>
            <SkeletonBlock className="h-4 w-16" />
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="flex justify-between gap-4">
                <SkeletonBlock className="h-3.5 w-14" />
                <SkeletonBlock className="h-3.5 w-28" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Loading>
  );
}

/** Blog cards (PostCard): the tinted top, then the title's two lines. */
function PostCardSkeleton() {
  return (
    <div className={cn(card, "flex flex-col gap-3 p-2.5")}>
      <SkeletonBlock className="h-24 w-full rounded-2xl" />
      <div className="flex flex-col gap-2 px-1 pb-1">
        <SkeletonBlock className="h-4 w-full" />
        <SkeletonBlock className="h-4 w-2/3" />
        <SkeletonBlock className="mt-1 h-3 w-1/3" />
      </div>
    </div>
  );
}

/** /blog and its categories: the title, the category chips, a grid of posts. */
export function BlogSkeleton() {
  return (
    <Loading className="gap-0">
      <TitleSkeleton />
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4">
        {Array.from({ length: 8 }, (_, i) => (
          <PostCardSkeleton key={i} />
        ))}
      </div>
    </Loading>
  );
}

/** A post: the crumbs, the tinted header card, then paragraphs. */
export function PostSkeleton() {
  return (
    <Loading className="gap-0">
      <div className="mb-4 flex gap-2">
        <SkeletonBlock className="h-8 w-20 rounded-full" />
        <SkeletonBlock className="h-8 w-24 rounded-full" />
      </div>
      <div className="items-start gap-6 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem]">
        <div className="flex min-w-0 flex-col gap-3">
          <div className={cn(card, "flex flex-col gap-3 p-5 sm:p-8")}>
            <SkeletonBlock className="h-8 w-4/5" />
            <SkeletonBlock className="h-4 w-full" />
            <SkeletonBlock className="h-4 w-3/5" />
            <div className="mt-3 flex items-center gap-2">
              <SkeletonBlock className="size-9 rounded-full" />
              <SkeletonBlock className="h-3.5 w-28" />
            </div>
          </div>
          <div className={cn(card, "flex flex-col gap-3 p-5 sm:p-8")}>
            {Array.from({ length: 7 }, (_, i) => (
              <SkeletonBlock key={i} className={cn("h-4", i % 4 === 3 ? "w-1/2" : "w-full")} />
            ))}
          </div>
        </div>
        <SkeletonBlock className="hidden h-64 rounded-card lg:block" />
      </div>
    </Loading>
  );
}

/** The cart and checkout: rows on one side, the order summary on the other. */
export function CartSkeleton({ rows = 2 }: { rows?: number }) {
  return (
    <Loading className="grid items-start gap-4 lg:grid-cols-[1fr_380px] lg:gap-6">
      <div className="flex flex-col gap-3">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className={cn(card, "flex gap-3 p-3 sm:p-4")}>
            <SkeletonBlock className="size-20 shrink-0 rounded-2xl sm:size-24" />
            <div className="flex flex-1 flex-col gap-2 py-1">
              <SkeletonBlock className="h-4 w-3/4" />
              <SkeletonBlock className="h-3 w-1/2" />
              <SkeletonBlock className="mt-auto h-5 w-1/3" />
            </div>
          </div>
        ))}
      </div>
      <div className={cn(card, "flex flex-col gap-3 p-4 sm:p-5")}>
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="flex justify-between gap-4">
            <SkeletonBlock className="h-3.5 w-20" />
            <SkeletonBlock className="h-3.5 w-24" />
          </div>
        ))}
        <SkeletonBlock className="mt-2 h-12 w-full rounded-full" />
      </div>
    </Loading>
  );
}
